import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useRestaurant } from "@/context/RestaurantContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import {
  ChefHat,
  ShoppingCart,
  History,
  LogOut,
  Home,
  Minus,
  Plus,
  Trash2,
  Package,
  TrendingUp,
  User,
  MapPin,
  ArrowLeftRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientLayout({ children }) {
  const { user, logout } = useAuth();
  const { items, updateQuantity, removeItem, clearCart, total, itemCount, setRestaurantScope } = useCart();
  const { selected: restaurant, clearSelection, restaurants } = useRestaurant();
  const [cartOpen, setCartOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setRestaurantScope(restaurant?.restaurant_id || null);
  }, [restaurant?.restaurant_id, setRestaurantScope]);

  const canSeePrices = restaurant?.show_prices_to_client !== false;
  const totalUnits = items.reduce((sum, item) => sum + item.quantity, 0);

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Sesión cerrada");
  };

  const handleSubmitOrder = async () => {
    if (items.length === 0) {
      toast.error("El carrito está vacío");
      return;
    }
    if (!restaurant) {
      toast.error("Selecciona un restaurante primero");
      return;
    }

    setSubmitting(true);
    try {
      const orderItems = items.map((item) => ({
        product_id: item.product_id,
        product_name: item.name,
        quantity: item.quantity,
        price: item.price,
        unit: item.unit,
      }));

      await axios.post(`${API}/client/orders`, {
        restaurant_id: restaurant.restaurant_id,
        items: orderItems,
        notes: notes,
      });

      toast.success("¡Pedido realizado con éxito!");
      clearCart();
      setNotes("");
      setCartOpen(false);
      navigate("/history");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al realizar el pedido");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3">
              <img 
                src="/logo.jpeg" 
                alt="Hermanos Cela" 
                className="h-10 object-contain"
              />
              <div className="hidden sm:block">
                <span className="text-lg font-bold text-slate-900">Cela Club</span>
                <span className="block text-xs text-slate-500">by Hermanos Cela</span>
              </div>
            </Link>

            {/* Restaurant indicator */}
            {restaurant && (
              <div className="hidden lg:flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-lg" data-testid="current-restaurant-badge">
                  <MapPin className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800 max-w-[180px] truncate">{restaurant.name}</span>
                </div>
                {restaurants.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { clearSelection(); clearCart(); }}
                    className="text-xs gap-1 text-slate-500"
                    data-testid="change-restaurant-btn"
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Cambiar</span>
                  </Button>
                )}
              </div>
            )}

            {/* Navigation */}
            <nav className="flex items-center gap-1">
              <Link to="/dashboard">
                <Button
                  variant={location.pathname === "/dashboard" ? "secondary" : "ghost"}
                  className="gap-2"
                  data-testid="nav-dashboard-btn"
                >
                  <Home className="w-4 h-4" />
                  <span className="hidden sm:inline">Pedido</span>
                </Button>
              </Link>
              <Link to="/history">
                <Button
                  variant={location.pathname === "/history" ? "secondary" : "ghost"}
                  className="gap-2"
                  data-testid="nav-history-btn"
                >
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline">Historial</span>
                </Button>
              </Link>
              <Link to="/stats">
                <Button
                  variant={location.pathname === "/stats" ? "secondary" : "ghost"}
                  className="gap-2"
                  data-testid="nav-stats-btn"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span className="hidden sm:inline">{canSeePrices ? "Mi Gasto" : "Mi Actividad"}</span>
                </Button>
              </Link>
              <Link to="/profile">
                <Button
                  variant={location.pathname === "/profile" ? "secondary" : "ghost"}
                  className="gap-2"
                  data-testid="nav-profile-btn"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Perfil</span>
                </Button>
              </Link>
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Cart button */}
              <Sheet open={cartOpen} onOpenChange={setCartOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    className="relative gap-2"
                    data-testid="cart-btn"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    <span className="hidden sm:inline">Carrito</span>
                    {itemCount > 0 && (
                      <span className="absolute -top-2 -right-2 w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {itemCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-lg flex flex-col overflow-x-hidden">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      Tu Pedido
                    </SheetTitle>
                  </SheetHeader>

                  {items.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
                      <Package className="w-16 h-16 text-slate-300 mb-4" />
                      <p className="text-slate-500">Tu carrito está vacío</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Añade productos desde tu catálogo
                      </p>
                    </div>
                  ) : (
                    <>
                      <ScrollArea className="flex-1 -mx-6 px-6">
                        <div className="space-y-3 py-4">
                          {items.map((item) => {
                            const isConsultar = item.price_source === "NONE" || (item.price === 0 && !item.price_source);
                            return (
                            <div
                              key={item.product_id}
                              className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-slate-50 rounded-lg"
                              data-testid={`cart-item-${item.product_id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 truncate">
                                  {item.name}
                                </p>
                                {!canSeePrices ? (
                                  <p className="text-sm text-slate-500">
                                    {item.quantity} {item.unit}
                                  </p>
                                ) : isConsultar ? (
                                  <p className="text-sm text-amber-600 font-medium">Consultar</p>
                                ) : (
                                  <p className="text-sm text-slate-500">
                                    {item.price.toFixed(2)}€ / {item.unit}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7 sm:h-8 sm:w-8"
                                  onClick={() =>
                                    updateQuantity(item.product_id, item.quantity - 1)
                                  }
                                  data-testid={`cart-minus-${item.product_id}`}
                                >
                                  <Minus className="w-4 h-4" />
                                </Button>
                                <span className="w-6 sm:w-8 text-center font-medium">
                                  {item.quantity}
                                </span>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7 sm:h-8 sm:w-8"
                                  onClick={() =>
                                    updateQuantity(item.product_id, item.quantity + 1)
                                  }
                                  data-testid={`cart-plus-${item.product_id}`}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 sm:h-8 sm:w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => removeItem(item.product_id)}
                                  data-testid={`cart-remove-${item.product_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                          })}
                        </div>
                      </ScrollArea>

                      <div className="border-t pt-4 space-y-4">
                        <div>
                          <label className="text-sm font-medium text-slate-700">
                            Notas del pedido (opcional)
                          </label>
                          <textarea
                            className="mt-1 w-full p-3 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={2}
                            placeholder="Ej: Entregar por la mañana..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="cart-notes-input"
                          />
                        </div>

                        <Separator />

                        {canSeePrices ? (
                          <>
                            <div className="flex items-center justify-between text-lg font-semibold">
                              <span>Total</span>
                              <span className="text-blue-600">{total.toFixed(2)}€</span>
                            </div>
                            <p className="text-xs text-slate-400 text-right">* Sin IVA</p>
                          </>
                        ) : (
                          <div className="rounded-2xl bg-slate-50 p-4" data-testid="cart-hidden-prices-summary">
                            <p className="text-sm text-slate-600">
                              {itemCount} producto{itemCount !== 1 ? "s" : ""} · {totalUnits} unidad{totalUnits !== 1 ? "es" : ""}
                            </p>
                          </div>
                        )}
                        {canSeePrices && items.some((i) => i.price_source === "NONE" || (i.price === 0 && !i.price_source)) && (
                          <p className="text-xs text-amber-600" data-testid="cart-consultar-note">
                            Hay productos marcados como "Consultar". Se incluirán en el pedido y te confirmaremos el precio.
                          </p>
                        )}

                        <Button
                          className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium text-base"
                          onClick={handleSubmitOrder}
                          disabled={submitting}
                          data-testid="submit-order-btn"
                        >
                          {submitting ? (
                            <div className="spinner" />
                          ) : (
                            <>
                              <Package className="w-5 h-5 mr-2" />
                              Realizar Pedido
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </SheetContent>
              </Sheet>

              {/* User menu */}
              <div className="hidden sm:flex items-center gap-2 pl-2 border-l">
                <span className="text-sm text-slate-600 max-w-[120px] truncate">
                  {user?.name}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="sm:hidden"
                data-testid="logout-btn-mobile"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>
    </div>
  );
}
